import type {
  AfterHoursCondition,
  AssignmentTypeCondition,
  HasTagCondition,
  LastOrderTimeCondition,
  MessageBodyMatchesCondition,
  MessageSentimentCondition,
  ReceivedAtEmailCondition,
  RuleCondition,
  SentFromEmailCondition,
  SubjectLineCondition,
  TicketChannelCondition,
  TicketStatusCondition,
  TopicCondition,
} from "@redotech/redo-flows-common/rules/schemas";
import {
  AssignmentTypeOption,
  assignmentTypeConditionSchema,
  hasTagConditionSchema,
  lastOrderTimeConditionSchema,
  messageBodyMatchesConditionSchema,
  messageSentimentConditionSchema,
  receivedAtEmailConditionSchema,
  sentFromEmailConditionSchema,
  subjectLineConditionSchema,
  ticketChannelConditionSchema,
  ticketStatusConditionSchema,
  topicConditionSchema,
} from "@redotech/redo-flows-common/rules/schemas";
import {
  DateQueryOperator,
  NumericQueryOperator,
  QueryOperator,
  RuleConditionType,
  type SelectionQueryOperator,
  type TextQueryOperator,
} from "@redotech/redo-flows-common/rules/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useMemo } from "react";
import { AfterHoursCondition as AfterHoursConditionComponent } from "../flow-editor/conditions/after-hours-condition";
import { AssignmentTypeCondition as AssignmentTypeConditionComponent } from "../flow-editor/conditions/assignment-type-condition";
import { HasTagCondition as HasTagConditionComponent } from "../flow-editor/conditions/has-tag-condition";
import { LastOrderTimeCondition as LastOrderTimeConditionComponent } from "../flow-editor/conditions/last-order-time-condition";
import { MessageBodyMatchesCondition as MessageBodyMatchesConditionComponent } from "../flow-editor/conditions/message-body-matches-condition";
import { MessageSentimentCondition as MessageSentimentConditionComponent } from "../flow-editor/conditions/message-sentiment-condition";
import { ReceivedAtEmailCondition as ReceivedAtEmailConditionComponent } from "../flow-editor/conditions/received-at-email-condition";
import { SentFromEmailCondition as SentFromEmailConditionComponent } from "../flow-editor/conditions/sent-from-email-condition";
import { SubjectLineCondition as SubjectLineConditionComponent } from "../flow-editor/conditions/subject-line-condition";
import { TicketChannelCondition as TicketChannelConditionComponent } from "../flow-editor/conditions/ticket-channel-condition";
import { TicketStatusCondition as TicketStatusConditionComponent } from "../flow-editor/conditions/ticket-status-condition";
import { TopicCondition as TopicConditionComponent } from "../flow-editor/conditions/topic-condition";

export interface ConditionItemProps {
  condition: RuleCondition;
  onChange: (condition: RuleCondition) => void;
  onDelete: () => void;
}

// Query operator options for selection-based conditions (is/is not)
export const selectionOperatorOptions = (): RedoListItem<QueryOperator>[] => [
  {
    id: QueryOperator.IS,
    type: "text",
    text: t("is"),
    value: QueryOperator.IS,
  },
  {
    id: QueryOperator.IS_NOT,
    type: "text",
    text: t("is not"),
    value: QueryOperator.IS_NOT,
  },
];

// Query operator options for text-based conditions
export const textOperatorOptions = (): RedoListItem<QueryOperator>[] => [
  {
    id: QueryOperator.CONTAINS,
    type: "text",
    text: t("contains"),
    value: QueryOperator.CONTAINS,
  },
  {
    id: QueryOperator.DOES_NOT_CONTAIN,
    type: "text",
    text: t("does not contain"),
    value: QueryOperator.DOES_NOT_CONTAIN,
  },
  {
    id: QueryOperator.IS_EXACTLY,
    type: "text",
    text: t("is exactly"),
    value: QueryOperator.IS_EXACTLY,
  },
  {
    id: QueryOperator.DOES_NOT_EQUAL,
    type: "text",
    text: t("does not equal"),
    value: QueryOperator.DOES_NOT_EQUAL,
  },
];

// Query operator options for date-based conditions (Last Order Time)
export const dateOperatorOptions = (): RedoListItem<DateQueryOperator>[] => [
  {
    id: DateQueryOperator.WITHIN_LAST,
    type: "text",
    text: t("Within last"),
    value: DateQueryOperator.WITHIN_LAST,
  },
  {
    id: DateQueryOperator.ON_OR_BEFORE,
    type: "text",
    text: t("On or before"),
    value: DateQueryOperator.ON_OR_BEFORE,
  },
  {
    id: DateQueryOperator.ON_OR_AFTER,
    type: "text",
    text: t("On or after"),
    value: DateQueryOperator.ON_OR_AFTER,
  },
  {
    id: DateQueryOperator.BETWEEN,
    type: "text",
    text: t("Between", { context: "comparison operator for a range" }),
    value: DateQueryOperator.BETWEEN,
  },
  {
    id: DateQueryOperator.NEVER,
    type: "text",
    text: t("Never ordered"),
    value: DateQueryOperator.NEVER,
  },
];

export type AnyOperator =
  | QueryOperator
  | DateQueryOperator
  | NumericQueryOperator;

// Query operator options for numeric-based conditions (order value, item count)
export const numericOperatorOptions =
  (): RedoListItem<NumericQueryOperator>[] => [
    {
      id: NumericQueryOperator.GREATER_THAN,
      type: "text",
      text: t("Greater than", { context: "comparison operator" }),
      value: NumericQueryOperator.GREATER_THAN,
    },
    {
      id: NumericQueryOperator.LESS_THAN,
      type: "text",
      text: t("Less than", { context: "comparison operator" }),
      value: NumericQueryOperator.LESS_THAN,
    },
    {
      id: NumericQueryOperator.EQUAL_TO,
      type: "text",
      text: t("Equal to", { context: "comparison operator" }),
      value: NumericQueryOperator.EQUAL_TO,
    },
    {
      id: NumericQueryOperator.BETWEEN,
      type: "text",
      text: t("Between", { context: "comparison operator for a range" }),
      value: NumericQueryOperator.BETWEEN,
    },
  ];

export function getOperatorOptionsForConditionType(
  conditionType: RuleConditionType,
): RedoListItem<AnyOperator>[] | null {
  switch (conditionType) {
    case RuleConditionType.TICKET_CHANNEL:
    case RuleConditionType.TICKET_STATUS:
    case RuleConditionType.USER_IS_AVAILABLE:
    case RuleConditionType.PREVIOUS_TICKET_STATUS:
    case RuleConditionType.TOPIC:
    case RuleConditionType.ASSIGNMENT_TYPE:
    case RuleConditionType.ASSIGNMENT:
    case RuleConditionType.MESSAGE_SENTIMENT:
    case RuleConditionType.HAS_TAG:
    case RuleConditionType.TRIGGER_TAG:
    case RuleConditionType.MESSAGE_TYPE:
    case RuleConditionType.AI_STATUS:
    case RuleConditionType.SHOPPER_AGENT_OUTREACH:
      return selectionOperatorOptions();
    case RuleConditionType.SUBJECT_LINE:
    case RuleConditionType.MESSAGE_BODY_MATCHES:
    case RuleConditionType.SENT_FROM_EMAIL:
    case RuleConditionType.RECEIVED_AT_EMAIL:
    case RuleConditionType.SENT_FROM:
    case RuleConditionType.RECEIVED_AT:
      return textOperatorOptions();
    case RuleConditionType.LAST_ORDER_TIME:
      return dateOperatorOptions();
    case RuleConditionType.MESSAGE_COUNT:
      return numericOperatorOptions();
    case RuleConditionType.AFTER_HOURS:
    case RuleConditionType.TIME_BASED:
    case RuleConditionType.LAST_ORDER:
    case RuleConditionType.RESPONSE_TIME:
    case RuleConditionType.RANDOM_PERCENTAGE:
    case RuleConditionType.SHOPPER_AGENT_CONVERSATION:
    case RuleConditionType.MESSAGE_STORY_KIND:
      return null;
  }
}

const conditionTypeOptions = (): {
  id: string;
  type: "text";
  text: string;
  value: RuleConditionType;
}[] => [
  {
    id: RuleConditionType.TICKET_CHANNEL,
    type: "text",
    text: t("Ticket channel"),
    value: RuleConditionType.TICKET_CHANNEL,
  },
  {
    id: RuleConditionType.TICKET_STATUS,
    type: "text",
    text: t("Ticket status"),
    value: RuleConditionType.TICKET_STATUS,
  },
  {
    id: RuleConditionType.TOPIC,
    type: "text",
    text: t("Topic"),
    value: RuleConditionType.TOPIC,
  },
  {
    id: RuleConditionType.SENT_FROM_EMAIL,
    type: "text",
    text: t("Sent from email"),
    value: RuleConditionType.SENT_FROM_EMAIL,
  },
  {
    id: RuleConditionType.RECEIVED_AT_EMAIL,
    type: "text",
    text: t("Received at email"),
    value: RuleConditionType.RECEIVED_AT_EMAIL,
  },
  {
    id: RuleConditionType.SUBJECT_LINE,
    type: "text",
    text: t("Subject line"),
    value: RuleConditionType.SUBJECT_LINE,
  },
  {
    id: RuleConditionType.MESSAGE_BODY_MATCHES,
    type: "text",
    text: t("Message body matches"),
    value: RuleConditionType.MESSAGE_BODY_MATCHES,
  },
  {
    id: RuleConditionType.AFTER_HOURS,
    type: "text",
    text: t("After hours"),
    value: RuleConditionType.AFTER_HOURS,
  },
  {
    id: RuleConditionType.ASSIGNMENT_TYPE,
    type: "text",
    text: t("Assignment type"),
    value: RuleConditionType.ASSIGNMENT_TYPE,
  },
  {
    id: RuleConditionType.LAST_ORDER_TIME,
    type: "text",
    text: t("Customer last order"),
    value: RuleConditionType.LAST_ORDER_TIME,
  },
  {
    id: RuleConditionType.MESSAGE_SENTIMENT,
    type: "text",
    text: t("Message sentiment"),
    value: RuleConditionType.MESSAGE_SENTIMENT,
  },
  {
    id: RuleConditionType.HAS_TAG,
    type: "text",
    text: t("Has tag"),
    value: RuleConditionType.HAS_TAG,
  },
];

export const ConditionItem = memo(function ConditionItem({
  condition,
  onChange,
  onDelete,
}: ConditionItemProps) {
  const { t } = useTranslation();
  const handleTypeChange = useCallback(
    (newType: RuleConditionType) => {
      let newCondition: RuleCondition;

      switch (newType) {
        case RuleConditionType.TICKET_CHANNEL:
          newCondition = {
            conditionType: RuleConditionType.TICKET_CHANNEL,
            channels: [],
            queryOperator: QueryOperator.IS,
          } satisfies TicketChannelCondition;
          break;
        case RuleConditionType.TICKET_STATUS:
          newCondition = {
            conditionType: RuleConditionType.TICKET_STATUS,
            statuses: [],
            queryOperator: QueryOperator.IS,
          } satisfies TicketStatusCondition;
          break;
        case RuleConditionType.TOPIC:
          newCondition = {
            conditionType: RuleConditionType.TOPIC,
            topics: [],
            queryOperator: QueryOperator.IS,
          } satisfies TopicCondition;
          break;
        case RuleConditionType.SENT_FROM_EMAIL:
          newCondition = {
            conditionType: RuleConditionType.SENT_FROM_EMAIL,
            emails: [],
            queryOperator: QueryOperator.CONTAINS,
          } satisfies SentFromEmailCondition;
          break;
        case RuleConditionType.RECEIVED_AT_EMAIL:
          newCondition = {
            conditionType: RuleConditionType.RECEIVED_AT_EMAIL,
            emails: [],
            queryOperator: QueryOperator.IS_EXACTLY,
          } satisfies ReceivedAtEmailCondition;
          break;
        case RuleConditionType.SUBJECT_LINE:
          newCondition = {
            conditionType: RuleConditionType.SUBJECT_LINE,
            queryStrings: [],
            caseSensitive: false,
            queryOperator: QueryOperator.CONTAINS,
          } satisfies SubjectLineCondition;
          break;
        case RuleConditionType.MESSAGE_BODY_MATCHES:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_BODY_MATCHES,
            queryStrings: [],
            caseSensitive: false,
            queryOperator: QueryOperator.CONTAINS,
          } satisfies MessageBodyMatchesCondition;
          break;
        case RuleConditionType.AFTER_HOURS:
          newCondition = {
            conditionType: RuleConditionType.AFTER_HOURS,
          } satisfies AfterHoursCondition;
          break;
        case RuleConditionType.ASSIGNMENT_TYPE:
          newCondition = {
            conditionType: RuleConditionType.ASSIGNMENT_TYPE,
            assignmentType: AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
            queryOperator: QueryOperator.IS,
          } satisfies AssignmentTypeCondition;
          break;
        case RuleConditionType.LAST_ORDER_TIME:
          newCondition = {
            conditionType: RuleConditionType.LAST_ORDER_TIME,
            queryOperator: DateQueryOperator.WITHIN_LAST,
          } satisfies LastOrderTimeCondition;
          break;
        case RuleConditionType.MESSAGE_SENTIMENT:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_SENTIMENT,
            sentiments: [],
            queryOperator: QueryOperator.IS,
          } satisfies MessageSentimentCondition;
          break;
        case RuleConditionType.HAS_TAG:
          newCondition = {
            conditionType: RuleConditionType.HAS_TAG,
            tags: [],
            queryOperator: QueryOperator.IS,
          } satisfies HasTagCondition;
          break;
        case RuleConditionType.TIME_BASED:
        case RuleConditionType.LAST_ORDER:
        case RuleConditionType.RESPONSE_TIME:
        case RuleConditionType.SENT_FROM:
        case RuleConditionType.RECEIVED_AT:
        case RuleConditionType.AI_STATUS:
        case RuleConditionType.ASSIGNMENT:
        case RuleConditionType.MESSAGE_COUNT:
        case RuleConditionType.TRIGGER_TAG:
        case RuleConditionType.MESSAGE_TYPE:
        case RuleConditionType.RANDOM_PERCENTAGE:
        case RuleConditionType.PREVIOUS_TICKET_STATUS:
        case RuleConditionType.SHOPPER_AGENT_CONVERSATION:
        case RuleConditionType.SHOPPER_AGENT_OUTREACH:
        case RuleConditionType.USER_IS_AVAILABLE:
        case RuleConditionType.MESSAGE_STORY_KIND:
          // V3-only or system-managed conditions
          return;
        default:
          return assertNever(newType);
      }

      onChange(newCondition);
    },
    [onChange],
  );

  const renderConditionDetails = useMemo(() => {
    if (!condition.conditionType) {
      return null;
    }

    const conditionType = condition.conditionType;

    switch (conditionType) {
      case RuleConditionType.AFTER_HOURS:
        return <AfterHoursConditionComponent />;
      case RuleConditionType.ASSIGNMENT_TYPE:
        return (
          <AssignmentTypeConditionComponent
            condition={assignmentTypeConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_BODY_MATCHES:
        return (
          <MessageBodyMatchesConditionComponent
            condition={messageBodyMatchesConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.RECEIVED_AT_EMAIL:
        return (
          <ReceivedAtEmailConditionComponent
            condition={receivedAtEmailConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.SENT_FROM_EMAIL:
        return (
          <SentFromEmailConditionComponent
            condition={sentFromEmailConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.SUBJECT_LINE:
        return (
          <SubjectLineConditionComponent
            condition={subjectLineConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.TICKET_CHANNEL:
        return (
          <TicketChannelConditionComponent
            condition={ticketChannelConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.TICKET_STATUS:
        return (
          <TicketStatusConditionComponent
            condition={ticketStatusConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.TOPIC:
        return (
          <TopicConditionComponent
            condition={topicConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.LAST_ORDER_TIME:
        return (
          <LastOrderTimeConditionComponent
            condition={lastOrderTimeConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_SENTIMENT:
        return (
          <MessageSentimentConditionComponent
            condition={messageSentimentConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      case RuleConditionType.HAS_TAG:
        return (
          <HasTagConditionComponent
            condition={hasTagConditionSchema.parse(condition)}
            onChange={onChange}
          />
        );
      // User-subject condition; only the v3 editor builds these rules.
      case RuleConditionType.USER_IS_AVAILABLE:
        return null;
      default:
        assertNever(conditionType);
    }
  }, [condition, onChange]);

  const operatorOptions = useMemo(() => {
    if (!condition.conditionType) {
      return null;
    }
    return getOperatorOptionsForConditionType(condition.conditionType);
  }, [condition.conditionType]);

  const handleOperatorChange = useCallback(
    (newOperator: AnyOperator) => {
      switch (condition.conditionType) {
        case RuleConditionType.AFTER_HOURS:
          return;
        case RuleConditionType.TICKET_CHANNEL:
        case RuleConditionType.TICKET_STATUS:
        case RuleConditionType.TOPIC:
        case RuleConditionType.ASSIGNMENT_TYPE:
        case RuleConditionType.MESSAGE_SENTIMENT:
        case RuleConditionType.HAS_TAG:
          onChange({
            ...condition,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            queryOperator: newOperator as SelectionQueryOperator,
          });
          break;
        case RuleConditionType.SUBJECT_LINE:
        case RuleConditionType.MESSAGE_BODY_MATCHES:
        case RuleConditionType.SENT_FROM_EMAIL:
        case RuleConditionType.RECEIVED_AT_EMAIL:
          onChange({
            ...condition,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            queryOperator: newOperator as TextQueryOperator,
          });
          break;
        case RuleConditionType.LAST_ORDER_TIME:
          onChange({
            ...condition,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            queryOperator: newOperator as DateQueryOperator,
          });
          break;
        case RuleConditionType.USER_IS_AVAILABLE:
          onChange({
            ...condition,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            queryOperator: newOperator as SelectionQueryOperator,
          });
          break;
        default:
          assertNever(condition);
      }
    },
    [condition, onChange],
  );

  const currentOperator = useMemo(() => {
    if (!condition.conditionType || !operatorOptions) {
      return undefined;
    }
    const operator =
      "queryOperator" in condition ? condition.queryOperator : undefined;
    return operatorOptions.find((opt) => opt.value === operator);
  }, [condition, operatorOptions]);

  return (
    <Flex
      align="flex-start"
      bgColor="secondary"
      borderRadius="md"
      dir="column"
      gap="lg"
      p="lg"
    >
      <Flex align="center" gap="sm" justify="space-between" w="full">
        <Flex align="center" gap="sm" grow={1}>
          <RedoSingleSelectDropdownInput
            options={conditionTypeOptions()}
            optionSelected={({ item }) => handleTypeChange(item.value)}
            placeholder={t("Select condition")}
            selectedItem={
              condition.conditionType
                ? conditionTypeOptions().find(
                    (opt) => opt.value === condition.conditionType,
                  )
                : undefined
            }
          />
          {operatorOptions && (
            <RedoSingleSelectDropdownInput
              options={operatorOptions}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              selectedItem={currentOperator}
            />
          )}
        </Flex>
        <RedoButton
          aria-label={t("Delete")}
          hierarchy="tertiary"
          IconLeading={TrashIcon}
          onClick={onDelete}
          size="sm"
          theme="destructive"
        />
      </Flex>

      {condition.conditionType && renderConditionDetails}
    </Flex>
  );
});
